import { useEffect } from "react";

import { FormattedMessage, useIntl } from "react-intl";

// @ts-ignore
import FeatureGates from "@atlaskit/feature-gate-js-client";

import { useLogMeInD0to6 } from "@/components/confluence/hooks/use-log-me-in-d0to6.tsx";
import { Bundle } from "@/components/signup/bxp-ui-signup/src/util";
import { wacToken } from "@/libraries/design/foundations";
import { getLocalizedImage } from "@/localization/localizable-images/paid-lp-conf/localized-images-conf-features";
import ImageChecklistSide from "@/patterns/image-checklist-side";
import { PaidHeroCarousel } from "@/patterns/paid-hero/src/paid-hero-carousel";
import {
    PaidLandingConfProps,
    PaidLandingConfTemplate
} from "@/templates/paid-landing/src/paid-conf.template";
import HeroSection from "@/templates/software/confluence/confluence-software-page/src/sections/log-me-in/hero";
import { AnchorContentsResponse } from "@/utils/anchor-queries/get-anchor-contents";
import { experiments, useExperimentExposure } from "@/utils/experiment";
import { FeatureExposed } from "@/utils/feature-exposed";

import { ConfluenceSoftwarePageProps } from "./confluence-software-page";
import { isTargetedSubAnchor } from "./subAnchors/subAnchor-util.tsx";
export interface ConfluencePagePaidLpConfFeaturesProps
    extends ConfluenceSoftwarePageProps {
    anchorContents?: AnchorContentsResponse | null;
    persona?: string;
    subAnchor?: string;
}

export const ConfluenceSoftwarePageVariantPaidLpConfFeatures = (
    pageProps: ConfluencePagePaidLpConfFeaturesProps
) => {
    const intl = useIntl();
    const [exposureSubAnchorFired, fireSubAnchorExposure] =
        useExperimentExposure(experiments.cfind3254LandingPageSubAnchor);
    const [brandAnchorExposureFired, fireBrandAnchorExposure] =
        useExperimentExposure(experiments.cfind3627BrandAnchorVisualCleanup);

    useEffect(() => {
        if (
            isTargetedSubAnchor(pageProps.subAnchor) &&
            !exposureSubAnchorFired
        ) {
            fireSubAnchorExposure();
        }
    }, [exposureSubAnchorFired, fireSubAnchorExposure]);

    useEffect(() => {
        if (!brandAnchorExposureFired) {
            fireBrandAnchorExposure();
        }
    }, [brandAnchorExposureFired, fireBrandAnchorExposure]);

    const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeInD0to6(
        {
            availableProducts: pageProps.availableProducts,
            userProfile: pageProps.userProfile
        }
    );

    const carouselLiftAndShift = [
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.FeaturesSection.start-with-page.title",
                defaultMessage: "Start with a page"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.FeaturesSection.start-with-page.body",
                defaultMessage:
                    "Plan projects, take notes, and brainstorm. Let AI write your first draft."
            }),
            // will fix in separate ticket GWP-4659

            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/cs8c8dbwh841g64nad3y8o63fo6peqkv.webp",
            imgAltText: "A Confluence page being edited"
        },
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.FeaturesSection.share-with-teams.title",
                defaultMessage: "Share with your teams"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.FeaturesSection.share-with-teams.body",
                defaultMessage:
                    "Invite your teams to collaborate using real-time editing and inline comments."
            }),
            // will fix in separate ticket GWP-4659

            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/63i2v45spmjd7q66mqefep624mf6y20h.webp",
            imgAltText:
                "A Confluence page with multiple teammates editing and leaving comments"
        },
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.FeaturesSection.connect-work.title",
                defaultMessage: "Connect all your work"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.FeaturesSection.connect-work.body",
                defaultMessage:
                    "Stay organized in dedicated workspaces, equipped with AI-powered search."
            }),
            // will fix in separate ticket GWP-4659

            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/o47q33qpw6cp3211df81a3noi34e41ib.webp",
            imgAltText:
                "A Confluence page with a summary being generated by Atlassian Intelligence"
        }
    ];

    const showDefault = !shouldShowLogMeIn;

    let customHeroSection = (
        <PaidHeroCarousel
            pageId={
                showDefault ? "confluence-default" : "paid-lp-conf-features"
            }
            embeddedSignup={{ bundle: "confluence" as Bundle }}
            carouselSlides={carouselLiftAndShift}
        />
    );

    if (shouldShowLogMeIn) {
        customHeroSection = (
            <FeatureExposed
                experimentKey="cfind-2801-get-paid-users-back-to-product"
                group="Test"
            >
                <HeroSection
                    confluenceSites={recentSites}
                    userProfileName={userProfileName}
                    hideExploreMore
                    loggedInPaid
                />
            </FeatureExposed>
        );
    }

    const props: PaidLandingConfProps = {
        customHeroSection,
        heroProps: {
            headerText: intl.formatMessage({
                id: "ConfluenceSoftwarePagePaidLpConfFeatures.heroHeader",
                defaultMessage: "Accomplish more together with Confluence"
            }),
            secondaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePagePaidLpConfFeatures.heroBody",
                defaultMessage:
                    "So many features, so little time — all built to meet your team’s needs"
            }),
            featuresLabel: intl.formatMessage({
                id: "ConfluenceSoftwarePagePaidLpConfFeatures.heroListHeading",
                defaultMessage: "What you get on the free plan:"
            }),
            featuresList: [
                intl.formatMessage({
                    id: "ConfluenceSoftwarePagePaidLpConfFeatures.heroListItem1",
                    defaultMessage: "Supports up to 10 users or 3 agents"
                }),
                intl.formatMessage({
                    id: "ConfluenceSoftwarePagePaidLpConfFeatures.heroListItem2",
                    defaultMessage: "Includes 2 GB of storage"
                }),
                intl.formatMessage({
                    id: "ConfluenceSoftwarePagePaidLpConfFeatures.heroListItem3",
                    defaultMessage: "Offers Community support"
                }),
                intl.formatMessage({
                    id: "ConfluenceSoftwarePagePaidLpConfFeatures.heroListItem4",
                    defaultMessage: "Is always free, no credit card needed"
                })
            ],
            signup: {
                bundle: "confluence",
                edition: "free"
            }
        },
        tabsProps: {
            dataProductKey: "confluence-PaidLpConflFeatures",
            headerText: intl.formatMessage({
                id: "ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsHeader",
                defaultMessage:
                    "Jump start company-wide collaboration with fewer tools by using Confluence."
            }),
            tabStyles: {
                // eslint-disable-next-line local/ensure-wac-token-usage
                defaultBG: "#ebecf0",
                // eslint-disable-next-line local/ensure-wac-token-usage
                defaultText: "#42526e",
                // eslint-disable-next-line local/ensure-wac-token-usage
                activeBG: "#0747a6",
                activeText: wacToken("color.neutral.light0"),
                // eslint-disable-next-line local/ensure-wac-token-usage
                hoverBG: "#ebecf0",
                // eslint-disable-next-line local/ensure-wac-token-usage
                hoverText: "#42526e"
            },
            styles: {
                "--lp-tabs-hover": "#c1c7d0",
                "--lp-tabs-unselected": "#6b778c"
            },
            tabs: [
                {
                    textLabel: intl.formatMessage({
                        id: "ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.pill.documentation",
                        defaultMessage: "Documentation"
                    }),
                    id: "tab-documentation",
                    component: Documentation()
                },
                {
                    textLabel: intl.formatMessage({
                        id: "ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.pill.collaboration",
                        defaultMessage: "Collaboration"
                    }),
                    id: "tab-collaboration",
                    component: Collaboration()
                },
                {
                    textLabel: intl.formatMessage({
                        id: "ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.pill.truth",
                        defaultMessage: "Single source of truth"
                    }),
                    id: "tab-truth",
                    component: Truth()
                },
                {
                    textLabel: intl.formatMessage({
                        id: "ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.pill.templates",
                        defaultMessage: "Templates"
                    }),
                    id: "tab-templates",
                    component: Templates()
                }
            ]
        },
        keyword: pageProps.keyword,
        subAnchor: pageProps.subAnchor
    };

    if (showDefault) {
        props.testimonialProps = undefined;
    }

    return <PaidLandingConfTemplate {...props} />;
};

/* Tabs */

const Documentation = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsHeader.documentation",
        defaultMessage: "Create better documentation for your teams"
    });

    const listItems = [
        <FormattedMessage
            key="listItem1"
            id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.documentation.listItem1"
            defaultMessage="Organize pages, attachments, and even spaces with keyword labels — such as meeting type or project name."
        />,
        <FormattedMessage
            key="listItem2"
            id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.documentation.listItem2"
            defaultMessage="Use the advanced search & page tree to quickly find and label key docs in a well-organized page tree."
        />,
        <FormattedMessage
            key="listItem3"
            id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.documentation.listItem3"
            defaultMessage="Use the Table of Contents macro to help colleagues quickly locate information."
        />
    ];

    const imageSrc = getLocalizedImage("documentation", intl.locale);
    const imageAlt = intl.formatMessage({
        id: "ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsImageAltText.documentation",
        defaultMessage: "Documentation"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};

const Collaboration = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsHeader.collaboration",
        defaultMessage: "Collaborate with teams in real-time"
    });

    const listItems = [
        <FormattedMessage
            key="listItem1"
            id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.collaboration.listItem1"
            defaultMessage="Co-edit together in real time and publish updates that highlight changes."
        />,
        <FormattedMessage
            key="listItem2"
            id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.collaboration.listItem2"
            defaultMessage="Make it a “team effort” with in-line and page comments, likes, and visual elements, such as images, GIFs, and emojis."
        />,
        <FormattedMessage
            key="listItem3"
            id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.collaboration.listItem3"
            defaultMessage="Alert your teammates when you tag them or assign a task so everyone stays on top of progress."
        />
    ];

    const imageSrc = getLocalizedImage("collaboration", intl.locale);
    const imageAlt = intl.formatMessage({
        id: "ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsImageAltText.collaboration",
        defaultMessage: "Project collaboration"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};

const Truth = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsHeader.truth",
        defaultMessage: "Share information between teams"
    });

    const listItems = [
        <FormattedMessage
            key="listItem1"
            id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.truth.listItem1"
            defaultMessage="Keep employees informed with open access to information they need, but share and protect sensitive content with permission settings."
        />,
        <FormattedMessage
            key="listItem2"
            id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.truth.listItem2"
            defaultMessage="Easily get back to recent spaces, in-progress work, and drafts, and stay updated with activity and popular feeds on your personal “homepage.”"
        />
    ];

    const imageSrc = getLocalizedImage("truth", intl.locale);
    const imageAlt = intl.formatMessage({
        id: "ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsImageAltText.truth",
        defaultMessage: "Program manager hub"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};

const Templates = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsHeader.templates",
        defaultMessage: "There’s a template for that"
    });

    const listItems = [
        <FormattedMessage
            key="listItem1"
            id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.templates.listItem1"
            defaultMessage="Use best-practices templates, from product roadmaps to marketing plans so you can quickly jump right in."
        />,
        <FormattedMessage
            key="listItem2"
            id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.templates.listItem2"
            defaultMessage="Kickstart the creation stage by spending less time formatting pages and more time getting sh*t done."
        />,
        <FormattedMessage
            key="listItem3"
            id="ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.templates.listItem3"
            defaultMessage="Start with a blank page or choose from over 75 customizable templates for every team–from strategy and planning docs to reports."
        />
    ];

    const imageSrc = getLocalizedImage("templates", intl.locale);
    const imageAlt = intl.formatMessage({
        id: "ConfluenceSoftwarePagePaidLpConfFeatures.tabSection.tabsImageAltText.templates",
        defaultMessage: "Confluence templates"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};
